import { NgIf } from '@angular/common';
import {
  Component,
  DestroyRef,
  ElementRef,
  Input,
  OnInit,
  ViewChild,
  inject,
} from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { SharedModule } from 'primeng/api';
import { InputOtpModule } from 'primeng/inputotp';
import { ValueAccessorDirective } from '../../directives/value-accessor.directive';
import {
  formErrorMessage,
  getVisibleFormErrorTypes,
} from '../../helpers/helper';
import { CustomErrorMessage } from '../../models/custom-error-messages.model';

type OtpEvents = {
  input: (event: Event) => void;
  keydown: (event: Event) => void;
  paste: (event: Event) => void;
};

@Component({
  selector: 'pa-input-otp',
  templateUrl: './pa-input-otp.component.html',
  styleUrls: ['./pa-input-otp.component.scss'],
  standalone: true,
  imports: [
    NgIf,
    ReactiveFormsModule,
    InputOtpModule,
    SharedModule,
  ],
  hostDirectives: [ValueAccessorDirective],
})
export class PaInputOtpComponent implements OnInit {
  @Input() length = 4;
  @Input() invalid = false;
  @Input() errorText = '';
  @Input() autocomplete = 'one-time-code';
  @Input() customErrorMessages: CustomErrorMessage | null = null;

  @ViewChild('otpContainer', { read: ElementRef })
  otpContainer?: ElementRef<HTMLElement>;

  valueAccessor = inject(ValueAccessorDirective<string>);
  showErrors = false;
  errors: string[] = [];

  private readonly destroy = inject(DestroyRef);
  private otpDeleteHandled = false;

  ngOnInit(): void {
    this.setupValidation();
  }

  get isDisabled(): boolean {
    return this.valueAccessor?.control?.disabled ?? false;
  }

  get showInvalid(): boolean {
    return this.invalid;
  }

  get displayErrorText(): string {
    if (this.errorText) {
      return this.errorText;
    }

    if (!this.showErrors || !this.errors.length) {
      return '';
    }

    return this.getErrorMessage(this.errors[0]);
  }

  handleInput(event: Event, events: OtpEvents): void {
    const input = event.target as HTMLInputElement;
    const digit = input.value.replace(/\D/g, '').slice(-1);
    if (input.value !== digit) {
      input.value = digit;
    }
    events.input(event);

    const inputType = (event as InputEvent).inputType;
    if (digit && !isCaretMoveInputType(inputType)) {
      this.focusSibling(input, 1);
    }
  }

  handleKeyDown(event: KeyboardEvent, events: OtpEvents): void {
    if (isOtpDeleteKey(event)) {
      event.preventDefault();
      this.deleteDigit(event.target as HTMLInputElement);
      return;
    }
    if (event.code === '' || event.code === 'Unidentified') {
      return;
    }
    events.keydown(event);
  }

  handleBeforeInput(event: Event): void {
    const inputType = (event as InputEvent).inputType;
    if (!isDeleteInputType(inputType)) {
      return;
    }
    event.preventDefault();
    this.deleteDigit(event.target as HTMLInputElement);
  }

  handleFocus(event: Event): void {
    (event.target as HTMLInputElement).select();
  }

  getFirstInput(): HTMLInputElement | null {
    return this.getInputs()[0] ?? null;
  }

  focusFirst(): HTMLInputElement | null {
    const first = this.getFirstInput();
    if (first && !first.disabled) {
      first.focus();
      return first;
    }

    return null;
  }

  clear(): void {
    this.valueAccessor.control?.setValue('', { emitEvent: false });
    this.clearNativeInputs();
    setTimeout(() => this.clearNativeInputs());
  }

  private setupValidation(): void {
    const control = this.valueAccessor?.control;
    if (!control) {
      return;
    }

    control.statusChanges
      .pipe(takeUntilDestroyed(this.destroy))
      .subscribe(() => this.updateErrorState());

    this.updateErrorState();
  }

  private updateErrorState(): void {
    const control = this.valueAccessor?.control;
    if (!control) {
      return;
    }

    this.showErrors = (control.touched || control.dirty) && control.invalid;
    this.errors = control.errors
      ? getVisibleFormErrorTypes(control.errors, this.customErrorMessages)
      : [];
  }

  private getErrorMessage(errorType: string): string {
    const error = this.valueAccessor?.control?.errors?.[errorType];
    return formErrorMessage(errorType, error, this.customErrorMessages);
  }

  private clearNativeInputs(): void {
    this.getInputs().forEach((input) => {
      input.value = '';
    });
  }

  private deleteDigit(input: HTMLInputElement): void {
    if (this.otpDeleteHandled) {
      return;
    }
    this.otpDeleteHandled = true;
    queueMicrotask(() => {
      this.otpDeleteHandled = false;
    });

    const inputs = this.getInputs();
    const target = input.value ? input : inputs[inputs.indexOf(input) - 1];
    if (!target) {
      return;
    }

    target.value = '';
    target.focus();
    this.valueAccessor.control?.setValue(
      inputs.map((item) => item.value).join(''),
    );
  }

  private getInputs(): HTMLInputElement[] {
    return Array.from(
      this.otpContainer?.nativeElement.querySelectorAll('input') ?? [],
    );
  }

  private focusSibling(input: HTMLInputElement, offset: number): void {
    const inputs = this.getInputs();
    inputs[inputs.indexOf(input) + offset]?.focus();
  }
}

function isOtpDeleteKey(event: KeyboardEvent): boolean {
  return (
    event.key === 'Backspace' ||
    event.key === 'Delete' ||
    event.code === 'Backspace' ||
    event.code === 'Delete'
  );
}

function isDeleteInputType(inputType: string): boolean {
  return (
    inputType === 'deleteContentBackward' ||
    inputType === 'deleteContentForward'
  );
}

function isCaretMoveInputType(inputType: string): boolean {
  return (
    inputType === 'insertText' ||
    inputType === 'deleteContentBackward' ||
    inputType === 'deleteContentForward'
  );
}
